import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { Modal } from '../../models/common';
import { HideOpenElementsService } from '../../services/hide-open-elements/hide-open-elements.service';
import { BackdropService } from '../../services/backdrop/backdrop.service';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Manages the modal component for an app.
 *
 * @description Use this service to show a new modal, or to navigate between modals in a workflow.
 * @export
 * @class ModalService
 */
@Injectable({ providedIn: 'root' })
export class ModalService {
    private _modalSubject = new ReplaySubject<Modal>(1);
    private _blockScrollSubject = new Subject<boolean>();
    public modal: Modal = null;
    public modalStream: Observable<Modal>;
    public blockScrollStream: Observable<boolean>;
    private _modalClosedSubject = new Subject<void>();
    public modalClosed$ = this._modalClosedSubject.asObservable();

    constructor(
        public backdropService: BackdropService,
        public hideOpenElementsService: HideOpenElementsService,
        private _router: Router
    ) {
        SingletonServiceGuard.register('ModalService');
        this.modalStream = this._modalSubject.asObservable();
        this.blockScrollStream = this._blockScrollSubject.asObservable();
        this.hideOpenElementsService.hideOpenElementsStream.subscribe(() => {
            this.closeModal();
        });

        // Close modal on navigation.
        this._router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe(() => {
            this.closeModal();
        });
    }

    public showModal(modal: Modal): void {
        this.modal = modal;
        this._modalSubject.next(this.modal);
    }

    public closeModal(): void {
        this._modalSubject.next(null);
        this.backdropService.closeBackDrop();
        this._modalClosedSubject.next();
    }

    public blockScrolling(): void {
        this._blockScrollSubject.next(true);
    }

    public unblockScrolling(): void {
        this._blockScrollSubject.next(false);
    }
}
